/**
 * ProjectDetailsStep - Step 1: Collect project name, description, industry
 */

import React, { useState } from 'react';
import type { ScratchFormData } from '../ScratchDetailsScreen';

interface ProjectDetailsStepProps {
  data: ScratchFormData;
  onChange: (updates: Partial<ScratchFormData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const INDUSTRIES = [
  'Automotive',
  'Aerospace',
  'Medical Devices',
  'Robotics',
  'Manufacturing',
  'Electronics',
  'Energy',
  'Other',
];

export function ProjectDetailsStep({ data, onChange, onNext, onBack }: ProjectDetailsStepProps) {
  const [errors, setErrors] = useState<{ name?: string }>({});

  const handleChange = (field: keyof typeof data.project, value: string) => {
    onChange({
      project: {
        ...data.project,
        [field]: value,
      },
    });

    // Clear error when user types
    if (field === 'name' && errors.name) {
      setErrors({});
    }
  };

  const handleNext = () => {
    // Validate
    const newErrors: { name?: string } = {};

    if (!data.project.name || data.project.name.trim().length < 3) {
      newErrors.name = 'Project name must be at least 3 characters';
    }

    if (data.project.name && data.project.name.length > 100) {
      newErrors.name = 'Project name must be less than 100 characters';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onNext();
  };

  const isValid = data.project.name.trim().length >= 3 && data.project.name.length <= 100;

  return (
    <div className="step-container" data-testid="project-details-step">
      <div>
        <h2 className="step-title">Let's start your FMEA</h2>
        <p className="step-subtitle">
          First, tell us about your project. This helps organize your FMEA and makes it easier to
          find later.
        </p>
      </div>

      <div className="step-form">
        {/* Project Name */}
        <div className="form-field">
          <label htmlFor="project-name">
            Project Name <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <input
            id="project-name"
            type="text"
            value={data.project.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="e.g., Robot Arm v2, Heart Monitor FMEA"
            data-testid="project-name-input"
            maxLength={100}
          />
          {errors.name && <span className="field-error">{errors.name}</span>}
          <span className="field-hint">
            Choose a descriptive name that identifies this FMEA project
          </span>
        </div>

        {/* Description */}
        <div className="form-field">
          <label htmlFor="project-description">Description (Optional)</label>
          <textarea
            id="project-description"
            value={data.project.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Brief description of what this FMEA covers..."
            data-testid="project-description-input"
            rows={3}
          />
          <span className="field-hint">
            Optional: Add details about the scope or purpose of this FMEA
          </span>
        </div>

        {/* Industry */}
        <div className="form-field">
          <label htmlFor="project-industry">Industry (Optional)</label>
          <select
            id="project-industry"
            value={data.project.industry}
            onChange={(e) => handleChange('industry', e.target.value)}
            data-testid="project-industry-select"
          >
            <option value="">Select an industry...</option>
            {INDUSTRIES.map((industry) => (
              <option key={industry} value={industry}>
                {industry}
              </option>
            ))}
          </select>
          <span className="field-hint">
            Industry selection helps us provide relevant templates later
          </span>
        </div>
      </div>

      <div className="step-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onBack}
          data-testid="back-button"
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleNext}
          disabled={!isValid}
          data-testid="next-button"
        >
          Next: Add Component
        </button>
      </div>
    </div>
  );
}
